import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { subscribeDokumente, uploadDokument, deleteDokument, toggleFavorit, createOrdner, subscribeOrdner, setDokumentOrdner, deleteOrdner, renameOrdner } from './dokumenteStorage';
import { useConfirm } from './components/ConfirmDialog';
import PdfVorschau from './components/PdfVorschau';
import './MeineDokumente.css';

const TABS = [
  { id: 'cv',            label: 'Lebensläufe',     pflicht: true,  beschreibung: 'Generierte und hochgeladene Lebensläufe' },
  { id: 'anschreiben',   label: 'Anschreiben',      pflicht: true,  beschreibung: 'Generierte und hochgeladene Anschreiben' },
  { id: 'zeugnisse',     label: 'Zeugnisse',        pflicht: false, beschreibung: 'Schul-, Hochschul- und Arbeitszeugnisse' },
  { id: 'fotos',         label: 'Bewerbungsfotos',  pflicht: false, beschreibung: 'Professionelle Bewerbungsfotos' },
  { id: 'bescheinigungen', label: 'Bescheinigungen', pflicht: false, beschreibung: 'Immatrikulation, Notenspiegel u.ä.' },
  { id: 'zertifikate',   label: 'Zertifikate',      pflicht: false, beschreibung: 'Sprach- und IT-Zertifikate' },
  { id: 'sonstige',      label: 'Sonstige',          pflicht: false, beschreibung: 'Führerschein, ID-Scans u.ä.' },
];

const STORAGE_KEY = 'dokumente_tabs_visible';
const FOTO_ACCEPT = 'image/jpeg,image/png,image/webp';
const FOTO_TYPES  = ['image/jpeg', 'image/png', 'image/webp'];
const FOTO_MAX_BYTES = 5 * 1024 * 1024;
const DOC_ACCEPT = '.pdf,.doc,.docx';
const DOC_TYPES  = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const DOC_MAX_BYTES = 20 * 1024 * 1024;

// Per-user storage limits
const LIMIT_BYTES = 30 * 1024 * 1024; // 30 MB total
const LIMIT_FILES = 50;                 // 50 uploaded files total

function formatBytes(bytes) {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


function istDocType(file) {
  if (DOC_TYPES.includes(file.type)) return true;
  const n = file.name.toLowerCase();
  return n.endsWith('.pdf') || n.endsWith('.doc') || n.endsWith('.docx');
}

function istFotoType(file) {
  if (FOTO_TYPES.includes(file.type)) return true;
  const n = file.name.toLowerCase();
  return n.endsWith('.jpg') || n.endsWith('.jpeg') || n.endsWith('.png') || n.endsWith('.webp');
}

// Grid positions for the 12-col × 10-row bento layout
const GRID_POSITIONS = {
  cv:              { col: '1 / 7',   row: '1 / 8'  },
  anschreiben:     { col: '7 / 13',  row: '1 / 5'  },
  zeugnisse:       { col: '7 / 10',  row: '5 / 8'  },
  fotos:           { col: '10 / 13', row: '5 / 8'  },
  bescheinigungen: { col: '1 / 5',   row: '8 / 11' },
  zertifikate:     { col: '5 / 9',   row: '8 / 11' },
  sonstige:        { col: '9 / 13',  row: '8 / 11' },
};

const ICON_PATHS = {
  cv:              "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  anschreiben:     "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
  zeugnisse:       "M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z",
  fotos:           "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z",
  bescheinigungen: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01",
  zertifikate:     "M12 14l9-5-9-5-9 5 9 5zm0 7l-9-5 9-5 9 5-9 5z",
  empfehlungen:    "M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z",
  sonstige:        "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z",
  word:            "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8 13h8v1H8v-1zm0 3h8v1H8v-1zm0-6h4v1H8v-1z",
};

function getDocumentIcon(mimeType) {
  if (!mimeType) return ICON_PATHS.sonstige;
  if (mimeType === 'application/pdf') return ICON_PATHS.cv;
  if (mimeType.includes('wordprocessingml') || mimeType === 'application/msword') return ICON_PATHS.word;
  if (mimeType.startsWith('image/')) return ICON_PATHS.fotos;
  if (mimeType.includes('opendocument')) return ICON_PATHS.bescheinigungen;
  return ICON_PATHS.sonstige;
}

const MORPH_TEXTS = {
  bescheinigungen: ['Immatrikulationsbescheinigung', 'Praktikumsbescheinigung', 'Arbeitsbescheinigung', 'Bescheinigungen'],
  zertifikate:     ['Sprachzertifikate', 'IT-Zertifikate', 'Zertifikate'],
  zeugnisse:       ['Arbeitszeugnis', 'Praktikumszeugnis', 'Ausbildungszeugnis', 'Zeugnisse'],
  anschreiben:     ['Motivationsschreiben', 'Trainee-Anschreiben', 'Anschreiben'],
  sonstige:        ['Arbeitsvertrag', 'Personalbogen', 'Sonstige'],
  fotos:           ['Lebenslauffotos', 'Bewerbungsfotos'],
};

const MORPH_TIME     = 1.0; // seconds per transition (two phases of 0.5s each)
const MORPH_COOLDOWN = 2.0; // seconds hold between transitions
const MORPH_DELAY_MS = 3000; // ms before first morph on hover

function applyMorphStyles(_container, s1, s2, fraction, from, to) {
  if (!s1 || !s2) return;
  s1.textContent = from;
  s2.textContent = to;
  const f1 = Math.max(0, 1 - fraction * 2);
  const f2 = Math.max(0, fraction * 2 - 1);
  s1.style.opacity = f1.toFixed(3);
  s1.style.filter  = `blur(${((1 - f1) * 8).toFixed(1)}px)`;
  s2.style.opacity = f2.toFixed(3);
  s2.style.filter  = `blur(${((1 - f2) * 8).toFixed(1)}px)`;
}

function settleSpans(_container, s1, s2, text) {
  if (!s1 || !s2) return;
  s2.textContent   = text;
  s2.style.filter  = '';
  s2.style.opacity = '1';
  s1.style.filter  = '';
  s1.style.opacity = '0';
}

function useMorphingTitle(tabId, hovered, containerRef) {
  const texts    = MORPH_TEXTS[tabId] || null;
  const span1Ref = useRef(null);
  const span2Ref = useRef(null);

  useEffect(() => {
    if (texts) settleSpans(containerRef?.current, span1Ref.current, span2Ref.current, texts[texts.length - 1]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!texts) return;
    const original  = texts[texts.length - 1];
    const container = containerRef?.current;
    const s1        = span1Ref.current;
    const s2        = span2Ref.current;
    let active  = true;
    let rafId   = null;
    let timerId = null;

    if (hovered) {
      timerId = setTimeout(() => {
        if (!active) return;
        let currentText = original; // what's currently shown/settled
        let nextText    = texts[0]; // what we'll morph to next
        let seqIndex    = 0;
        let morph       = 0;
        let cooldown    = 0;
        let prevTime    = null;

        const tick = (now) => {
          if (!active) return;
          if (prevTime === null) prevTime = now;
          const dt = Math.min((now - prevTime) / 1000, 0.1);
          prevTime = now;

          if (cooldown > 0) {
            cooldown -= dt;
            // Do NOT touch spans during cooldown — just wait
            rafId = requestAnimationFrame(tick);
            return;
          }

          morph = Math.min(morph + dt / MORPH_TIME, 1);
          applyMorphStyles(container, s1, s2, morph, currentText, nextText);

          if (morph >= 1) {
            currentText = nextText;
            settleSpans(container, s1, s2, currentText);
            if (currentText === original) { active = false; return; }
            seqIndex++;
            nextText = texts[seqIndex % texts.length];
            morph    = 0;
            cooldown = MORPH_COOLDOWN;
          }

          rafId = requestAnimationFrame(tick);
        };

        rafId = requestAnimationFrame(tick);
      }, MORPH_DELAY_MS);

    } else {
      const currentText = s2?.textContent || original;
      if (currentText === original) {
        settleSpans(container, s1, s2, original);
      } else {
        let fromText = currentText;
        let morph    = 0;
        let prevTime = null;

        const tick = (now) => {
          if (!active) return;
          if (prevTime === null) prevTime = now;
          const dt = Math.min((now - prevTime) / 1000, 0.1);
          prevTime = now;

          morph = Math.min(morph + dt / MORPH_TIME, 1);
          applyMorphStyles(container, s1, s2, morph, fromText, original);

          if (morph >= 1) { settleSpans(container, s1, s2, original); active = false; return; }
          rafId = requestAnimationFrame(tick);
        };

        rafId = requestAnimationFrame(tick);
      }
    }

    return () => {
      active = false;
      clearTimeout(timerId);
      cancelAnimationFrame(rafId);
    };
  }, [hovered]); // eslint-disable-line react-hooks/exhaustive-deps

  return { span1Ref, span2Ref };
}

function formatDatum(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d) / 86400000);
  if (diff === 0) return 'heute';
  if (diff === 1) return 'gestern';
  if (diff < 7)  return `vor ${diff} Tagen`;
  return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}

function getTileSub(tab, generiert, uploaded) {
  const total = generiert.length + uploaded.length;
  if (tab.id === 'cv') {
    if (total === 0) return tab.beschreibung;
    return `${total} Version${total !== 1 ? 'en' : ''}`;
  }
  if (tab.id === 'anschreiben') {
    if (generiert.length > 0) return `Zuletzt: ${generiert[0].name || 'Dokument'}`;
    return tab.beschreibung;
  }
  if (tab.id === 'fotos') {
    if (uploaded.length > 0) return `${uploaded.length} Foto${uploaded.length !== 1 ? 's' : ''}`;
    return tab.beschreibung;
  }
  if (tab.id === 'bescheinigungen') {
    if (uploaded.length > 0) {
      const gesamt = Math.max(4, uploaded.length);
      const offen = gesamt - uploaded.length;
      return `${uploaded.length} von ${gesamt} · ${offen} ausstehend`;
    }
    return tab.beschreibung;
  }
  if (tab.id === 'zertifikate') {
    if (uploaded.length > 0) {
      const names = uploaded.slice(0, 3).map(d => d.filename?.replace(/\.[^.]+$/, '') || '').filter(Boolean);
      const rest = uploaded.length - names.length;
      return [names.join(', '), rest > 0 ? `+${rest}` : ''].filter(Boolean).join(' ');
    }
    return tab.beschreibung;
  }
  if (tab.id === 'sonstige') {
    if (total === 0) return 'Führerschein, ID — noch leer';
    return `${total} Datei${total !== 1 ? 'en' : ''}`;
  }
  return tab.beschreibung;
}

function getTileUpdated(tab, generiert, uploaded) {
  const showFor = ['cv', 'anschreiben', 'fotos'];
  if (!showFor.includes(tab.id)) return null;
  if (tab.id === 'fotos') {
    const d = uploaded[0];
    if (d?.uploadedAt?.toDate) return formatDatum(d.uploadedAt.toDate().toISOString());
    return null;
  }
  const latest = generiert[0];
  if (latest) return formatDatum(latest.generiert_am || latest.erstellt_am);
  return null;
}

function countCvPages(stelle) {
  if (stelle.cv_data?.sektionen) return Math.max(1, Math.ceil(stelle.cv_data.sektionen.length / 3));
  if (stelle.cv_rohtext) return Math.max(1, Math.ceil(stelle.cv_rohtext.length / 1800));
  return 1;
}

function getInitialen(vor, nach) {
  return ((vor?.[0] || '') + (nach?.[0] || '')).toUpperCase() || '?';
}

// ─── MorphingLabel ────────────────────────────────────────────────────────────
function MorphingLabel({ fallbackLabel }) {
  return <h3 className="md-tile-label">{fallbackLabel}</h3>;
}

// ─── Check SVG ────────────────────────────────────────────────────────────────
const CheckSvg = () => (
  <svg viewBox="0 0 10 10" fill="none" width="7" height="7">
    <polyline points="1.5,5 4,7.5 8.5,2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Mini CV Page (for Lebenslauf animation) ─────────────────────────────────
const MiniCvPage = () => (
  <div className="md-mp">
    <div className="md-mp-head">
      <div className="md-mp-avatar"></div>
      <div><div className="md-mp-name"></div><div className="md-mp-sub-line"></div></div>
    </div>
    <div className="md-mp-rule"></div>
    <div className="md-mp-section"></div>
    <div className="md-mp-line"></div>
    <div className="md-mp-line short"></div>
    <div className="md-mp-line"></div>
    <div className="md-mp-section"></div>
    <div className="md-mp-line"></div>
    <div className="md-mp-line short"></div>
    <div className="md-mp-line faint"></div>
  </div>
);

// ─── Animation Components ─────────────────────────────────────────────────────
const AnimLebenslauf = () => (
  <div className="md-an-lv">
    <div className="md-an-lv-paper md-an-lv-3"><MiniCvPage /></div>
    <div className="md-an-lv-paper md-an-lv-2"><MiniCvPage /></div>
    <div className="md-an-lv-paper md-an-lv-1"><MiniCvPage /></div>
  </div>
);

const AnimAnschreiben = () => (
  <div className="md-an-env">
    <div className="md-an-env-back"></div>
    <div className="md-an-env-stamp">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="5" width="18" height="14" rx="2"/>
        <path d="m3 7 9 6 9-6"/>
      </svg>
    </div>
    <div className="md-an-env-letter">
      <div className="md-an-env-letter-from">Max Mustermann</div>
      <div className="md-an-env-letter-to">An: CV-Flow AG</div>
      <div className="md-an-env-letter-rule"></div>
      <div className="md-an-env-letter-line"></div>
      <div className="md-an-env-letter-line short"></div>
      <div className="md-an-env-letter-line"></div>
      <div className="md-an-env-letter-line tiny"></div>
      <div className="md-an-env-letter-sig"></div>
    </div>
    <div className="md-an-env-front"></div>
  </div>
);

const AnimZeugnisse = () => (
  <div className="md-an-zeu">
    <div className="md-an-zeu-roll">
      <div className="md-an-zeu-head">Abiturzeugnis</div>
      <div className="md-an-zeu-grade">1,7</div>
      <div className="md-an-zeu-school">Goethe-Gymnasium · 2019</div>
      <div className="md-an-zeu-seal"><CheckSvg /></div>
    </div>
  </div>
);

const AnimZertifikate = () => (
  <div className="md-an-zer">
    <div className="md-an-zer-cert">
      <div className="md-an-zer-title">Zertifikat</div>
      <div className="md-an-zer-line"></div>
      <div className="md-an-zer-name-real">IELTS · C1</div>
      <div className="md-an-zer-line short"></div>
      <div className="md-an-zer-seal">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 6 9 17l-5-5"/></svg>
      </div>
    </div>
    <div className="md-an-zer-cert">
      <div className="md-an-zer-ribbon"></div>
      <div className="md-an-zer-title">Zertifikat</div>
      <div className="md-an-zer-line"></div>
      <div className="md-an-zer-name-real">AWS SAA-C03</div>
      <div className="md-an-zer-line short"></div>
      <div className="md-an-zer-seal">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 6 9 17l-5-5"/></svg>
      </div>
    </div>
    <div className="md-an-zer-cert">
      <div className="md-an-zer-title">Zertifikat</div>
      <div className="md-an-zer-line"></div>
      <div className="md-an-zer-name-real">Scrum · PSM I</div>
      <div className="md-an-zer-line short"></div>
      <div className="md-an-zer-seal">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 6 9 17l-5-5"/></svg>
      </div>
    </div>
  </div>
);

const AnimBescheinigungen = () => (
  <div className="md-an-bes">
    <div className="md-an-bes-doc">
      <div className="md-an-bes-header"></div>
      <div className="md-an-bes-lines">
        <div className="md-an-bes-line"></div>
        <div className="md-an-bes-line"></div>
        <div className="md-an-bes-line short"></div>
        <div className="md-an-bes-line"></div>
        <div className="md-an-bes-line short2"></div>
      </div>
    </div>
    <div className="md-an-bes-stamp">
      <div className="md-an-bes-stamp-ring"></div>
      <div className="md-an-bes-stamp-core">
        <div className="md-an-bes-stamp-l"></div>
        <div className="md-an-bes-stamp-l s"></div>
        <div className="md-an-bes-stamp-l"></div>
      </div>
    </div>
  </div>
);

const AnimEmpfehlungen = () => (
  <div className="md-an-emp">
    <div className="md-an-emp-star">
      <svg viewBox="0 0 24 24" width="20" height="20">
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" fill="currentColor"/>
      </svg>
    </div>
    <div className="md-an-emp-star">
      <svg viewBox="0 0 24 24" width="20" height="20">
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" fill="currentColor"/>
      </svg>
    </div>
    <div className="md-an-emp-star">
      <svg viewBox="0 0 24 24" width="20" height="20">
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" fill="currentColor"/>
      </svg>
    </div>
  </div>
);

const AnimFotos = () => (
  <div className="md-an-fot">
    <div className="md-an-fot-card md-an-fot-c1"><div className="md-an-fot-img"></div></div>
    <div className="md-an-fot-card md-an-fot-c2"><div className="md-an-fot-img"></div></div>
    <div className="md-an-fot-card md-an-fot-c3"><div className="md-an-fot-img"></div></div>
    <div className="md-an-fot-more">+3</div>
  </div>
);

const AnimSonstige = () => (
  <div className="md-an-son">
    <div className="md-an-son-back"></div>
    <div className="md-an-son-tab"></div>
    <div className="md-an-son-doc"></div>
    <div className="md-an-son-front"></div>
  </div>
);

const TILE_ANIMS = {
  cv:              AnimLebenslauf,
  anschreiben:     AnimAnschreiben,
  zeugnisse:       AnimZeugnisse,
  fotos:           AnimFotos,
  bescheinigungen: AnimBescheinigungen,
  zertifikate:     AnimZertifikate,
  empfehlungen:    AnimEmpfehlungen,
  sonstige:        AnimSonstige,
};

// ─── FilterPopover ────────────────────────────────────────────────────────────
function FilterPopover({ tabsVisible, onToggle, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [onClose]);

  return (
    <div className="md-filter-popover" ref={ref}>
      <span className="md-filter-title">Kategorien verwalten</span>
      <span className="md-filter-hint">Blende nicht relevante Kategorien aus.</span>
      <div className="md-filter-list">
        {TABS.map(t => (
          <label key={t.id} className={`md-filter-item ${t.pflicht ? 'is-locked' : ''}`}>
            <input type="checkbox" checked={tabsVisible[t.id] !== false} disabled={t.pflicht} onChange={() => onToggle(t.id)} />
            <span className="md-filter-label">{t.label}</span>
            {t.pflicht && <span className="md-filter-lock">Pflicht</span>}
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── BentoCard ────────────────────────────────────────────────────────────────
function BentoCard({ tab, stellen, lebenslaufEntwuerfe = [], dokumente, onUpload, onDelete, onStelleOeffnen, onTileClick }) {
  const inputRef = useRef(null);
  const tileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [tileHovered, setTileHovered] = useState(false);

  const pos = GRID_POSITIONS[tab.id] || { col: 'auto', row: 'auto' };
  const uploaded = dokumente.filter(d => d.kategorie === tab.id);

  let generiert = [];
  if (tab.id === 'cv') {
    generiert = stellen
      .filter(s => s.cv_data || s.cv_rohtext)
      .concat(lebenslaufEntwuerfe)
      .sort((a, b) => new Date(b.generiert_am || b.erstellt_am || 0) - new Date(a.generiert_am || a.erstellt_am || 0));
  } else if (tab.id === 'anschreiben') {
    generiert = stellen
      .filter(s => s.anschreiben || s.anschreiben_rohtext)
      .sort((a, b) => new Date(b.generiert_am || b.erstellt_am || 0) - new Date(a.generiert_am || a.erstellt_am || 0));
  }

  const totalDocs = generiert.length + uploaded.length;
  const fotoLimit = tab.id === 'fotos' && uploaded.length >= 3;
  const isEmpty = totalDocs === 0;
  const isLarge = tab.id === 'cv' || tab.id === 'anschreiben';
  const isSonstigeEmpty = tab.id === 'sonstige' && isEmpty;

  const subText = getTileSub(tab, generiert, uploaded);
  const updatedText = getTileUpdated(tab, generiert, uploaded);

  const Anim = TILE_ANIMS[tab.id];

  const handleFiles = useCallback(async (files) => {
    if (!files?.length) return;
    console.log('[Upload] handleFiles aufgerufen, Anzahl:', files.length, 'Kategorie:', tab.id);
    setBusy(true); setFehler('');
    try {
      for (const f of files) {
        console.log('[Upload] Datei:', f.name, 'Typ:', f.type, 'Größe:', f.size);
        if (tab.id === 'fotos') {
          if (!istFotoType(f)) { console.warn('[Upload] Typ abgelehnt (Foto):', f.type); setFehler('Nur JPEG, PNG oder WebP erlaubt.'); continue; }
          if (f.size > FOTO_MAX_BYTES) { setFehler('Max. 5 MB pro Foto.'); continue; }
        } else {
          if (!istDocType(f)) { console.warn('[Upload] Typ abgelehnt (Dokument):', f.type, f.name); setFehler('Nur PDF oder Word-Dateien (.pdf, .doc, .docx) erlaubt.'); continue; }
          if (f.size > DOC_MAX_BYTES) { setFehler('Max. 20 MB pro Dokument.'); continue; }
        }
        console.log('[Upload] Sende an Backend...');
        setFehler('');
        const result = await onUpload(tab.id, f);
        console.log('[Upload] Erfolgreich:', result);
      }
    } catch (e) {
      console.error('[Upload] Fehler:', e);
      setFehler(e?.message || 'Upload fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }, [tab.id, onUpload]);

  useEffect(() => {
    const tile = tileRef.current;
    if (!tile) return;
    const onEnter = (e) => { e.preventDefault(); if (!fotoLimit) setDragOver(true); };
    const onOver  = (e) => { e.preventDefault(); if (!fotoLimit) setDragOver(true); };
    const onLeave = (e) => { if (!tile.contains(e.relatedTarget)) setDragOver(false); };
    const onDrop  = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const files = Array.from(e.dataTransfer?.files || []);
      if (!fotoLimit) handleFiles(files);
    };
    tile.addEventListener('dragenter', onEnter);
    tile.addEventListener('dragover',  onOver);
    tile.addEventListener('dragleave', onLeave);
    tile.addEventListener('drop',      onDrop);
    return () => {
      tile.removeEventListener('dragenter', onEnter);
      tile.removeEventListener('dragover',  onOver);
      tile.removeEventListener('dragleave', onLeave);
      tile.removeEventListener('drop',      onDrop);
    };
  }, [fotoLimit, handleFiles]);

  return (
    <div
      ref={tileRef}
      className={`md-tile${dragOver ? ' is-drag' : ''}${isLarge ? ' is-large' : ''}`}
      style={{ gridColumn: pos.col, gridRow: pos.row, cursor: 'pointer' }}
      onMouseEnter={() => setTileHovered(true)}
      onMouseLeave={() => setTileHovered(false)}
      onClick={() => onTileClick && onTileClick(tab.id)}
    >
      <input
        ref={inputRef}
        type="file"
        multiple={tab.id !== 'fotos'}
        hidden
        accept={tab.id === 'fotos' ? FOTO_ACCEPT : DOC_ACCEPT}
        onChange={(e) => handleFiles(Array.from(e.target.files || []))}
      />

      {/* Empty chip for Sonstige */}
      {isSonstigeEmpty && (
        <div className="md-tile-empty-chip">
          <svg viewBox="0 0 24 24" fill="none" width="10" height="10">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
          </svg>
          Hinzufügen
        </div>
      )}

      {/* Top: small icon chip + doc count */}
      <div className="md-tile-top">
        <div className="md-tile-icon-chip">
          <svg viewBox="0 0 24 24" fill="none" width="13" height="13">
            <path d={ICON_PATHS[tab.id]} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div className="md-tile-count">{isEmpty ? '—' : totalDocs}</div>
      </div>

      {/* Animation / Content area */}
      <div className="md-tile-anim">
        <div className={`md-tile-drop-hint${dragOver ? ' is-visible' : ''}`}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 16V4m0 0l-4 4m4-4l4 4M4 20h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Datei ablegen</span>
        </div>
        {Anim && <Anim />}
      </div>


      {/* Footer: title + sub + meta (timestamp + arrow) */}
      <div className="md-tile-foot">
        <div className="md-tile-text">
          <MorphingLabel tabId={tab.id} fallbackLabel={tab.label} hovered={tileHovered} />
          {fehler && <span className="md-tile-error">{fehler}</span>}
          {fotoLimit && <span className="md-tile-desc">Max. 3 Fotos erreicht</span>}
        </div>
        <div className="md-tile-meta">
          {updatedText && <span className="md-tile-updated">{updatedText}</span>}
          <button
            className="md-tile-arrow"
            onClick={(e) => { e.stopPropagation(); !busy && !fotoLimit && inputRef.current?.click(); }}
            title="Datei hochladen"
            disabled={busy || fotoLimit}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FolderCard ───────────────────────────────────────────────────────────────
function FolderCard({ name, fileCount, onClick, onDrop, onDelete, onRename }) {
  const [dragOver, setDragOver] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(name);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (renaming) { setNewName(name); setTimeout(() => inputRef.current?.select(), 0); }
  }, [renaming, name]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const confirmRename = () => {
    const trimmed = newName.trim();
    if (trimmed && trimmed !== name) onRename(trimmed);
    setRenaming(false);
  };

  return (
    <div
      className={`md-folder-card${dragOver ? ' is-drop-target' : ''}`}
      onClick={() => { if (!renaming) onClick(); }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); onDrop(e); }}
    >
      <div className="md-folder-card-menu-wrap" ref={menuRef}>
        <button
          className="md-folder-card-menu-btn"
          title="Optionen"
          onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
            <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
          </svg>
        </button>
        {menuOpen && (
          <div className="md-folder-card-menu" onClick={e => e.stopPropagation()}>
            <button onClick={() => { setMenuOpen(false); setRenaming(true); }}>
              <svg viewBox="0 0 24 24" fill="none" width="12" height="12"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
              Umbenennen
            </button>
            <button className="danger" onClick={() => { setMenuOpen(false); onDelete(); }}>
              <svg viewBox="0 0 24 24" fill="none" width="12" height="12"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
              Löschen
            </button>
          </div>
        )}
      </div>
      <div className="md-folder-card-icon">
        <svg viewBox="0 0 24 20" fill="none" width="72" height="60">
          <path d="M1 5.5 Q1 3 3.5 3 L9.5 3 L11.5 1 L21.5 1 Q23 1 23 2.5 L23 5.5 Z" fill="currentColor" opacity="0.55"/>
          <path d="M0 7 Q0 5 2 5 L22 5 Q24 5 24 7 L24 18 Q24 20 22 20 L2 20 Q0 20 0 18 Z" fill="currentColor"/>
        </svg>
        <svg className="md-folder-card-icon-overlay" viewBox="0 0 24 24" fill="none" width="22" height="22">
          <path d="M12 8v8M8 12h8" stroke="white" strokeWidth="1.8" strokeLinecap="round" opacity="0.5"/>
        </svg>
      </div>
      {renaming ? (
        <input
          ref={inputRef}
          className="md-folder-card-name-input"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape') setRenaming(false); }}
          onBlur={confirmRename}
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <div className="md-folder-card-name">{name}</div>
      )}
      <div className="md-folder-card-count">{fileCount} {fileCount === 1 ? 'Datei' : 'Dateien'}</div>
    </div>
  );
}

// ─── NewFolderCard ────────────────────────────────────────────────────────────
function NewFolderCard({ onConfirm, onCancel }) {
  const [name, setName] = useState('Neuer Ordner');
  const confirmed = useRef(false);

  const confirm = () => {
    if (confirmed.current) return;
    confirmed.current = true;
    onConfirm(name.trim() || 'Neuer Ordner');
  };

  return (
    <div className="md-folder-card is-new">
      <div className="md-folder-card-icon">
        <svg viewBox="0 0 24 20" fill="none" width="72" height="60">
          <path d="M1 5.5 Q1 3 3.5 3 L9.5 3 L11.5 1 L21.5 1 Q23 1 23 2.5 L23 5.5 Z" fill="currentColor" opacity="0.35"/>
          <path d="M0 7 Q0 5 2 5 L22 5 Q24 5 24 7 L24 18 Q24 20 22 20 L2 20 Q0 20 0 18 Z" fill="currentColor" opacity="0.6"/>
        </svg>
      </div>
      <input
        className="md-folder-card-name-input"
        value={name}
        autoFocus
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') confirm(); if (e.key === 'Escape') onCancel(); }}
        onBlur={confirm}
        onClick={e => e.stopPropagation()}
      />
    </div>
  );
}

// ─── BackButton ───────────────────────────────────────────────────────────────
function BackButton({ folderName, onBack, onRename, dragging }) {
  const [drop, setDrop] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(folderName);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (renaming) { setNewName(folderName); setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 0); }
  }, [renaming, folderName]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const confirmRename = () => {
    const trimmed = newName.trim();
    if (trimmed && trimmed !== folderName) onRename?.(trimmed);
    setRenaming(false);
  };

  return (
    <div
      className={`md-folder-path-back${drop ? ' is-drop-target' : dragging ? ' is-dragging' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDrop(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDrop(false); }}
      onDrop={(e) => {
        e.preventDefault(); setDrop(false);
        const docId = e.dataTransfer.getData('docId');
        if (docId) setDokumentOrdner(docId, null);
      }}
    >
      <button className="md-folder-path-back-btn" onClick={onBack}>
        <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        {drop ? 'Ablegen zum Herausnehmen' : 'Zurück'}
      </button>
      <span className="md-folder-path-sep" />
      {renaming ? (
        <input
          ref={inputRef}
          className="md-folder-path-rename-input"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape') setRenaming(false); }}
          onBlur={confirmRename}
        />
      ) : (
        <span className="md-folder-path-name">{folderName}</span>
      )}
      <div className="md-folder-path-menu-wrap" ref={menuRef}>
        <button className="md-folder-path-menu-btn" onClick={() => setMenuOpen(v => !v)}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13">
            <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
          </svg>
        </button>
        {menuOpen && (
          <div className="md-folder-card-menu">
            <button onClick={() => { setMenuOpen(false); setRenaming(true); }}>
              <svg viewBox="0 0 24 24" fill="none" width="12" height="12"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
              Umbenennen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DrawerPanel ──────────────────────────────────────────────────────────────
function DrawerPanel({ tab, dokumente, lebenslaufEntwuerfe, onStelleOeffnen, view, setView, onUpload, onDelete, onClose, usedBytes, usedFiles }) {
  const confirm = useConfirm();
  const [folder, setFolder] = useState('all');
  const [ordnerListe, setOrdnerListe] = useState([]);
  const [neuerOrdnerModus, setNeuerOrdnerModus] = useState(false);
  const [dragDocId, setDragDocId] = useState(null);
  const [search, setSearch] = useState('');
  const [uploadFehler, setUploadFehler] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [nurFavoriten, setNurFavoriten] = useState(false);

  const handleDrawerFiles = useCallback((files) => {
    let runningBytes = usedBytes;
    let runningFiles = usedFiles;
    for (const f of files) {
      if (runningFiles >= LIMIT_FILES) {
        setUploadFehler(`Limit erreicht: Maximal ${LIMIT_FILES} Dateien pro Konto.`); return;
      }
      if (runningBytes + f.size > LIMIT_BYTES) {
        setUploadFehler(`Speicherlimit erreicht: Maximal ${formatBytes(LIMIT_BYTES)} pro Konto. Noch frei: ${formatBytes(Math.max(0, LIMIT_BYTES - runningBytes))}.`); return;
      }
      if (tab.id === 'fotos') {
        if (!istFotoType(f)) { setUploadFehler('Nur JPEG, PNG oder WebP erlaubt.'); return; }
        if (f.size > FOTO_MAX_BYTES) { setUploadFehler('Max. 5 MB pro Foto.'); return; }
      } else {
        if (!istDocType(f)) { setUploadFehler('Nur PDF oder Word-Dateien (.pdf, .doc, .docx) erlaubt.'); return; }
        if (f.size > DOC_MAX_BYTES) { setUploadFehler('Max. 20 MB pro Dokument.'); return; }
      }
      setUploadFehler('');
      onUpload(tab.id, f);
      runningBytes += f.size;
      runningFiles += 1;
    }
  }, [tab.id, onUpload, usedBytes, usedFiles]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    const unsub = subscribeOrdner(tab.id, setOrdnerListe);
    return unsub;
  }, [tab.id]);

  const uploaded = dokumente.filter(d => d.kategorie === tab.id);
  const entwuerfe = tab.id === 'cv'
    ? (lebenslaufEntwuerfe || []).filter(s => !search || (s.name || '').toLowerCase().includes(search.toLowerCase()))
    : [];
  const totalCount = uploaded.length + entwuerfe.length;

  const gefilterteDokumente = uploaded
    .filter(d => !search || (d.filename || '').toLowerCase().includes(search.toLowerCase()))
    .filter(d => !nurFavoriten || d.favorit);
  const filteredUp = gefilterteDokumente.filter(d =>
    folder === 'all' ? !d.ordner : d.ordner === folder
  );

  const [drawerDragOver, setDrawerDragOver] = useState(false);

  return (
    <>
      <div className="md-drawer-backdrop" onClick={onClose} />
      <div
        className="md-drawer"
        onDragOver={(e) => { e.preventDefault(); setDrawerDragOver(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDrawerDragOver(false); }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDrawerDragOver(false);
          const files = Array.from(e.dataTransfer?.files || []);
          if (files.length) handleDrawerFiles(files);
        }}
        style={drawerDragOver ? { outline: '2px dashed var(--c-amber, #FF6600)', outlineOffset: -4 } : undefined}
      >
        {/* Header */}
        <div className="md-dr-head">
          <div className="md-dr-head-left">
            <div className="md-dr-head-icon">
              <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
                <path d={ICON_PATHS[tab.id]} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <div className="md-dr-crumbs">
                <span>Meine Dokumente</span>
                <svg viewBox="0 0 24 24" fill="none" width="9" height="9"><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/></svg>
                <span>{tab.label}</span>
              </div>
              <h2 className="md-dr-title">{tab.label}</h2>
              <p className="md-dr-sub">{totalCount} Datei{totalCount !== 1 ? 'en' : ''}{tab.id === 'cv' ? ' · Standard hilft der KI, deinen bevorzugten Lebenslauf zu nutzen' : ''}</p>
            </div>
          </div>
          <button className="md-dr-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </button>
        </div>

        {/* Storage usage bar */}
        {(() => {
          const pct = Math.min(100, (usedBytes / LIMIT_BYTES) * 100);
          const filePct = Math.min(100, (usedFiles / LIMIT_FILES) * 100);
          const isWarn = pct > 75 || filePct > 75;
          const isFull = pct >= 100 || filePct >= 100;
          return (
            <div className={`md-usage-bar-row${isWarn ? ' warn' : ''}${isFull ? ' full' : ''}`}>
              <div className="md-usage-bar-track">
                <div className="md-usage-bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <span className="md-usage-label">
                {formatBytes(usedBytes)} / {formatBytes(LIMIT_BYTES)}
                <span className="md-usage-files"> · {usedFiles}/{LIMIT_FILES} Dateien</span>
              </span>
            </div>
          );
        })()}

        {/* Upload Error Banner */}
        {uploadFehler && <div className="md-dr-warning">{uploadFehler}</div>}

        {/* Toolbar */}
        <div className="md-dr-tools">
          <div className="md-dr-search">
            <svg viewBox="0 0 24 24" fill="none" width="13" height="13"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.8"/><path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`In ${tab.label} suchen…`}/>
          </div>
          <div style={{flex:1}}/>
          <div className="md-dr-segment">
            {[
              { v:'grid', icon:"M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" },
              { v:'large', icon:"M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z" },
              { v:'list', icon:"M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" },
            ].map(({ v, icon }) => (
              <button key={v} className={view === v ? 'active' : ''} onClick={() => setView(v)}>
                <svg viewBox="0 0 24 24" fill="none" width="18" height="18"><path d={icon} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
              </button>
            ))}
          </div>
          {folder === 'all' && (
            <button className="md-dr-btn ghost" onClick={() => setNeuerOrdnerModus(true)}>
              <svg viewBox="0 0 24 24" fill="none" width="12" height="12"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
              Neuer Ordner
            </button>
          )}
          <button
            className={`md-dr-btn ghost${nurFavoriten ? ' aktiv' : ''}`}
            onClick={() => setNurFavoriten(v => !v)}
            title="Nur Favoriten"
          >
            <svg viewBox="0 0 24 24" fill={nurFavoriten ? 'currentColor' : 'none'} width="13" height="13">
              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" stroke="currentColor" strokeWidth="1.8"/>
            </svg>
            Favoriten
          </button>
          <label className="md-dr-btn primary">
            <svg viewBox="0 0 24 24" fill="none" width="12" height="12"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
            Hochladen
            <input type="file" multiple hidden accept={tab.id === 'fotos' ? FOTO_ACCEPT : DOC_ACCEPT} onChange={(e) => { handleDrawerFiles(Array.from(e.target.files || [])); e.target.value = ''; }}/>
          </label>
        </div>

        {/* Body */}
        <div className="md-dr-body">
          {/* Back button when inside a folder — also a drop target to eject files */}
          {folder !== 'all' && (
            <BackButton
              folderName={folder}
              onBack={() => setFolder('all')}
              dragging={!!dragDocId}
              onRename={(newName) => {
                const affectedIds = uploaded.filter(d => d.ordner === folder).map(d => d.id);
                renameOrdner(tab.id, folder, newName, affectedIds).then(() => setFolder(newName));
              }}
            />
          )}

          {totalCount === 0 && ordnerListe.length === 0 && !neuerOrdnerModus ? (
            <div className="md-dr-empty">
              <div className="md-dr-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" width="32" height="32"><path d={ICON_PATHS[tab.id]} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
              </div>
              <p>Noch keine Dateien in dieser Kategorie</p>
              <label className="md-dr-btn primary">
                <svg viewBox="0 0 24 24" fill="none" width="12" height="12"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                Erste Datei hochladen
                <input type="file" multiple hidden accept={tab.id === 'fotos' ? FOTO_ACCEPT : DOC_ACCEPT} onChange={(e) => { handleDrawerFiles(Array.from(e.target.files || [])); e.target.value = ''; }}/>
              </label>
            </div>
          ) : folder !== 'all' ? (
            /* ── Inside a folder ─────────────────────────────── */
            filteredUp.length === 0 ? (
              <div className="md-folder-empty-hint">
                <svg viewBox="0 0 24 24" fill="none" width="40" height="40"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                <p>Ordner ist leer — Dateien hierher ziehen</p>
              </div>
            ) : (
              <div className={`md-dr-grid view-${view}`}>
                {filteredUp.map(d => {
                  const date = d.uploadedAt?.toDate ? formatDatum(d.uploadedAt.toDate().toISOString()) : '';
                  const sizeKb = d.size ? `${(d.size / (1024 * 1024)).toFixed(2)} MB` : null;
                  return (
                    <div
                      key={d.id} className="md-dr-card"
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('docId', d.id); setDragDocId(d.id); }}
                      onDragEnd={() => setDragDocId(null)}
                      style={dragDocId === d.id ? { opacity: 0.5 } : {}}
                    >
                      <div className="md-dr-card-preview" onClick={() => setPreviewDoc(d)} style={{ cursor: 'pointer' }}><MiniCvPage /></div>
                      <div className="md-dr-card-body">
                        <div className="md-dr-card-header">
                          <svg className="md-doc-type-icon" viewBox="0 0 24 24" fill="none" width="16" height="16"><path d={getDocumentIcon(d.type)} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          <h4 className="md-dr-card-name">{d.filename}</h4>
                        </div>
                        <div className="md-dr-card-meta">
                          {sizeKb && <span>{sizeKb}</span>}
                          {date && <><span className="dot">·</span><span>{date}</span></>}
                        </div>
                      </div>
                      <div className="md-dr-card-actions">
                        <button title="Vorschau" onClick={() => setPreviewDoc(d)}><svg viewBox="0 0 24 24" fill="none" width="17" height="17"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="1.8"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8"/></svg></button>
                        <button className={`md-favorit-btn${d.favorit ? ' aktiv' : ''}`} title={d.favorit ? 'Aus Favoriten entfernen' : 'Favorisieren'} onClick={() => toggleFavorit(d.id, !d.favorit)}><svg viewBox="0 0 24 24" fill={d.favorit ? 'currentColor' : 'none'} width="17" height="17"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg></button>
                        <a href={d.url} target="_blank" rel="noreferrer" className="md-dr-action-link"><svg viewBox="0 0 24 24" fill="none" width="17" height="17"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg></a>
                        <button title="Löschen" onClick={async () => { if (await confirm(`„${d.filename}" löschen?`)) onDelete(d); }}><svg viewBox="0 0 24 24" fill="none" width="17" height="17"><polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="1.8"/></svg></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            /* ── Root view ───────────────────────────────────── */
            <>
              {/* Lebenslauf-Entwürfe (leere Lebensläufe ohne generierten Inhalt) */}
              {entwuerfe.length > 0 && (
                <>
                  <div className="md-dr-section-label" style={{ marginBottom: 10 }}>Entwürfe</div>
                  <div className={`md-dr-grid view-${view}`} style={{ marginBottom: 24 }}>
                    {entwuerfe.map(s => (
                      <div
                        key={s.id} className="md-dr-card md-dr-card--entwurf"
                        onClick={() => onStelleOeffnen && onStelleOeffnen(s)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="md-dr-card-preview">
                          <div className="md-dr-entwurf-blatt" />
                        </div>
                        <div className="md-dr-card-body">
                          <div className="md-dr-card-header">
                            <h4 className="md-dr-card-name">{s.name || 'Neuer Lebenslauf'}</h4>
                          </div>
                          <div className="md-dr-card-meta">
                            <span className="md-dr-entwurf-badge">Entwurf</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Folder icons */}
              {(ordnerListe.length > 0 || neuerOrdnerModus) && (
                <>
                  {filteredUp.length > 0 && (
                    <div className="md-dr-section-label" style={{ marginBottom: 10 }}>Ordner</div>
                  )}
                  <div className={`md-dr-grid view-${view}`} style={{ marginBottom: 24 }}>
                    {ordnerListe.map(name => (
                      <FolderCard
                        key={name}
                        name={name}
                        fileCount={uploaded.filter(d => d.ordner === name).length}
                        onClick={() => setFolder(name)}
                        onDrop={(e) => {
                          const docId = e.dataTransfer.getData('docId');
                          if (docId) setDokumentOrdner(docId, name);
                        }}
                        onDelete={async () => {
                          if (!await confirm(`Ordner „${name}" wirklich löschen?`)) return;
                          const affectedIds = uploaded.filter(d => d.ordner === name).map(d => d.id);
                          deleteOrdner(tab.id, name, affectedIds);
                        }}
                        onRename={(newName) => {
                          const affectedIds = uploaded.filter(d => d.ordner === name).map(d => d.id);
                          renameOrdner(tab.id, name, newName, affectedIds);
                        }}
                      />
                    ))}
                    {neuerOrdnerModus && (
                      <NewFolderCard
                        onConfirm={async (name) => {
                          if (name && !ordnerListe.includes(name)) {
                            await createOrdner(tab.id, name);
                          }
                          setNeuerOrdnerModus(false);
                        }}
                        onCancel={() => setNeuerOrdnerModus(false)}
                      />
                    )}
                  </div>
                </>
              )}

              {/* Uploaded docs */}
              {filteredUp.length > 0 && (
                <>
                  {ordnerListe.length > 0 && <div className="md-dr-section-label" style={{ marginBottom: 10 }}>Dateien</div>}
                  <div className={`md-dr-grid view-${view}`}>
                    {filteredUp.map(d => {
                      const date = d.uploadedAt?.toDate ? formatDatum(d.uploadedAt.toDate().toISOString()) : '';
                      const sizeKb = d.size ? `${(d.size / (1024 * 1024)).toFixed(2)} MB` : null;
                      return (
                        <div
                          key={d.id} className="md-dr-card"
                          draggable={ordnerListe.length > 0}
                          onDragStart={(e) => { e.dataTransfer.setData('docId', d.id); setDragDocId(d.id); }}
                          onDragEnd={() => setDragDocId(null)}
                          style={dragDocId === d.id ? { opacity: 0.5 } : {}}
                        >
                          <div className="md-dr-card-preview" onClick={() => setPreviewDoc(d)} style={{ cursor: 'pointer' }}><MiniCvPage /></div>
                          <div className="md-dr-card-body">
                            <div className="md-dr-card-header">
                              <svg className="md-doc-type-icon" viewBox="0 0 24 24" fill="none" width="16" height="16"><path d={getDocumentIcon(d.type)} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              <h4 className="md-dr-card-name">{d.filename}</h4>
                            </div>
                            <div className="md-dr-card-meta">
                              {sizeKb && <span>{sizeKb}</span>}
                              {date && <><span className="dot">·</span><span>{date}</span></>}
                            </div>
                          </div>
                          <div className="md-dr-card-actions">
                            <button title="Vorschau" onClick={() => setPreviewDoc(d)}><svg viewBox="0 0 24 24" fill="none" width="17" height="17"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="1.8"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8"/></svg></button>
                            <button className={`md-favorit-btn${d.favorit ? ' aktiv' : ''}`} title={d.favorit ? 'Aus Favoriten entfernen' : 'Favorisieren'} onClick={() => toggleFavorit(d.id, !d.favorit)}><svg viewBox="0 0 24 24" fill={d.favorit ? 'currentColor' : 'none'} width="17" height="17"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg></button>
                            <a href={d.url} target="_blank" rel="noreferrer" className="md-dr-action-link"><svg viewBox="0 0 24 24" fill="none" width="17" height="17"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg></a>
                            <button title="Löschen" onClick={async () => { if (await confirm(`„${d.filename}" löschen?`)) onDelete(d); }}><svg viewBox="0 0 24 24" fill="none" width="17" height="17"><polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="1.8"/></svg></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {filteredUp.length === 0 && search && (
                <p className="md-dr-no-results">Keine Ergebnisse für „{search}"</p>
              )}
            </>
          )}
        </div>
      </div>
      <PdfVorschau doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    </>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function MeineDokumente({ nutzer, aktivesProfile, stellen, lebenslaufEntwuerfe = [], onStelleOeffnen }) {
  const [filterOffen, setFilterOffen] = useState(false);
  const [drawerTabId, setDrawerTabId] = useState(null);
  const [drawerView, setDrawerView] = useState('grid');
  const [defaultCvId, setDefaultCvId] = useState(null);
  const [dokumente, setDokumente] = useState([]);
  const [subFehler, setSubFehler] = useState('');
  const [tabsVisible, setTabsVisible] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      return TABS.reduce((acc, t) => ({ ...acc, [t.id]: stored[t.id] !== false }), {});
    } catch {
      return TABS.reduce((acc, t) => ({ ...acc, [t.id]: true }), {});
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tabsVisible));
  }, [tabsVisible]);


  useEffect(() => {
    if (!nutzer) return undefined;
    const unsub = subscribeDokumente(
      (docs) => { setSubFehler(''); setDokumente(docs); },
      (err) => { setSubFehler(err?.message || 'Verbindung zu Dokumenten fehlgeschlagen'); setDokumente([]); },
    );
    return () => { try { unsub?.(); } catch { /* ignore */ } };
  }, [nutzer]);

  const usedBytes = useMemo(() => dokumente.reduce((s, d) => s + (d.size || 0), 0), [dokumente]);
  const usedFiles = dokumente.length;

  const handleUpload = useCallback((kategorie, file) => uploadDokument(kategorie, file), []);
  const handleDelete = useCallback((doc) => deleteDokument(doc), []);

  const toggleTab = (id) => {
    const t = TABS.find(x => x.id === id);
    if (t?.pflicht) return;
    setTabsVisible(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const sichtbareTabs = useMemo(
    () => TABS.filter(t => t.pflicht || tabsVisible[t.id] !== false),
    [tabsVisible]
  );

  const totalDokumente = useMemo(() => dokumente.length + stellen.filter(s => s.cv_data || s.cv_rohtext || s.anschreiben || s.anschreiben_rohtext).length + lebenslaufEntwuerfe.length, [dokumente, stellen, lebenslaufEntwuerfe]);

  const vorname  = aktivesProfile?.vorname  || nutzer?.displayName?.split(' ')[0] || '';
  const nachname = aktivesProfile?.nachname || nutzer?.displayName?.split(' ').slice(1).join(' ') || '';
  const beruf = aktivesProfile?.beruf || '';
  const gefuellt = sichtbareTabs.filter(t => {
    const gen = stellen.filter(s => t.id === 'cv' ? (s.cv_data || s.cv_rohtext) : t.id === 'anschreiben' ? (s.anschreiben || s.anschreiben_rohtext) : false);
    const up = dokumente.filter(d => d.kategorie === t.id);
    const entw = t.id === 'cv' ? lebenslaufEntwuerfe.length : 0;
    return (gen.length + up.length + entw) > 0;
  }).length;

  return (
    <div className="md-root">
      <img className="md-watermark" src="/logo.png" alt="" aria-hidden="true" />
      {/* Header */}
      <div className="md-head">
        <div className="md-head-left">
          <h1 className="md-head-title">
            Meine Dokumente
          </h1>
        </div>
        <div className="md-head-actions">
          <div className="md-filter-wrap" style={{ position: 'relative' }}>
            {filterOffen && (
              <FilterPopover tabsVisible={tabsVisible} onToggle={toggleTab} onClose={() => setFilterOffen(false)} />
            )}
          </div>
        </div>
      </div>

      {/* Subscription error banner */}
      {subFehler && (
        <div style={{ margin: '0 0 16px', padding: '10px 16px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', fontSize: 13 }}>
          Verbindungsfehler: {subFehler}
        </div>
      )}

      {/* Bento Grid */}
      <div className="md-bento-grid">
        {sichtbareTabs.map(tab => (
          <BentoCard
            key={tab.id}
            tab={tab}
            stellen={stellen}
            lebenslaufEntwuerfe={lebenslaufEntwuerfe}
            dokumente={dokumente}
            onUpload={handleUpload}
            onDelete={handleDelete}
            onStelleOeffnen={onStelleOeffnen}
            onTileClick={setDrawerTabId}
          />
        ))}
      </div>

      {/* Detail Drawer */}
      {drawerTabId && (() => {
        const tab = TABS.find(t => t.id === drawerTabId);
        if (!tab) return null;
        return (
          <DrawerPanel
            tab={tab}
            dokumente={dokumente}
            lebenslaufEntwuerfe={lebenslaufEntwuerfe}
            onStelleOeffnen={onStelleOeffnen}
            view={drawerView}
            setView={setDrawerView}
            onUpload={handleUpload}
            onDelete={handleDelete}
            onClose={() => setDrawerTabId(null)}
            usedBytes={usedBytes}
            usedFiles={usedFiles}
          />
        );
      })()}
    </div>
  );
}
